import classNames from 'classnames';
import _ from 'lodash';
import React, { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useRecoilState, useRecoilValue } from 'recoil';
import InboxZero from '../../components/InboxZero';
import { InputSearch } from '../../components/inputs/InputSearch';
import { Container } from '../../components/layout/ContainerFlex';
import LoadingIndicator from '../../components/LoadingIndicator';
import { ActionBar } from '../../components/nav/ActionBar';
import ActualityAggregatedStatsItem from '../components/ActualityAggregatedStatsItem';
import AggregationControls from '../components/AggregationControls';
import {
  actualityAggregatedStatsBySelectedGroup,
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsSearchTerm,
  actualityAggregatedStatsSelectedGroups,
} from '../store/actuality.kpi.state';
type ActualityAggregatedStatsPanelProps = {
  displayMode?: 'summary' | 'details' | 'process' | 'events';
  filterString?: string;
};

const ActualityAggregatedStatsGroupsList: React.FC<
  ActualityAggregatedStatsPanelProps
> = props => {
  const { t } = useTranslation('feed');
  const virtuoso = useRef<VirtuosoHandle>(null);
  const aggregatedStats = useRecoilValue(
    actualityAggregatedStatsBySelectedGroup,
  );
  const [aggregateBy, setAggregateBy] = useRecoilState(
    actualityAggregatedStatsGroupBy,
  );
  const [selectedGroups, setSelectedGroups] = useRecoilState(
    actualityAggregatedStatsSelectedGroups,
  );

  const hasData = _.size(aggregatedStats) > 0;

  const searchRegex = new RegExp(props.filterString, 'i');
  const filteredStats = _.isEmpty(props.filterString)
    ? aggregatedStats
    : _.filter(aggregatedStats, stats =>
        searchRegex.test(_.get(stats, aggregateBy)?.toString()),
      );

  const filteredGroupsCount = _.size(filteredStats);

  if (!hasData) {
    return <InboxZero selfCenter message={t`No events`} />;
  }
  if (filteredGroupsCount === 0) {
    return <InboxZero selfCenter message={t`No resources found`} />;
  }
  return (
    <div
      data-component="ActualityAggregatedStatsGroupsList"
      className={classNames('h-full overflow-x-hidden pb-[8rem]')}
    >
      {/* <Button
        className={classNames(
          'hover:text-menu-active',
          'bg-menu/50 hover:bg-menu',
          'text-xxs',
          'flex items-center',
          'rounded-full',
          'px-2 py-1',
          'focus:outline-none focus:ring-0',
        )}
        buttonSize="xs"
        hasIconAfter
        buttonIcon={
          <Icon.CloudDownloadCsv
            className={cn(
              'h-4 w-4 fill-current',
              // showStats ? '' : 'opacity-50 hover:opacity-85',
            )}
          />
        }
        onPress={() =>
          exportAsCSV({
            data: _.map(aggregatedStats, row => _.values(row)),
            // headers: _.map(visibleColumns, c => c.title),
          })
        }
      /> */}
      <Virtuoso
        style={{ height: '100%' }}
        className={classNames('h-full overflow-x-hidden')}
        ref={virtuoso}
        data={filteredStats}
        itemContent={(index, stats) => {
          return (
            <ActualityAggregatedStatsItem
              key={`stats-${aggregateBy}-${stats[aggregateBy]}`}
              data={stats}
              keyFields={[aggregateBy]}
              colorFields={[aggregateBy]}
              displayMode={props.displayMode}
              supportedColorBy={['group']}
              selected={selectedGroups}
              onSelectionChanged={v => setSelectedGroups(v)}
              mode="summary"
              defaultFieldsLimit={3}
              // hasCategoryStats={true}
              summaryFields={[
                // 'analyzedCost',
                'analyzedDuration',
                'analyzedDistance',
                'netUomsPerformance',
                'durationDiff',
              ]}
              className="mb-1"
            />
          );
        }}
      />
    </div>
  );
};

const ActualityAggregatedStatsGroupsSection: React.FC<
  ActualityAggregatedStatsPanelProps
> = props => {
  const { t } = useTranslation('feed');
  const [searchTerm, setSearchTerm] = useRecoilState(
    actualityAggregatedStatsSearchTerm,
  );

  return (
    <>
      <AggregationControls />
      <ActionBar className="flex flex-col gap-1 p-2" transparent>
        <InputSearch
          placeholder={t('Search Resources...')}
          value={searchTerm}
          onChange={(value: string) => setSearchTerm(value)}
          panelTitleMode
          classNamePlaceholder="!left-2 !top-2"
          classNameInput="!px-2 !py-1"
        />
        {/* {searchTerm && searchTerm.length > 0 && (
          <div
            data-component="ActualityAggregatedStatsGroupsSectionSearchInfo"
            className="text-menu-text/70 bg-menu/20 w-full rounded px-2 py-1 text-xs"
          >
            {t(
              'Searching in: Job ID, Agent, Location, SKU, Process Type, etc.',
            )}
          </div>
        )} */}
      </ActionBar>
      <Suspense
        fallback={
          <Container col className="relative !p-2">
            <LoadingIndicator
              absolute
              message={t`Loading Data...`}
              selfCenter
            />
          </Container>
        }
      >
        <ActualityAggregatedStatsGroupsList
          displayMode={props.displayMode}
          filterString={searchTerm}
        />
      </Suspense>
    </>
  );
};

export default ActualityAggregatedStatsGroupsSection;
